"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type KeyboardEvent as ReactKbEvent,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { AnimatePresence, motion } from "framer-motion";
import {
  Modal,
  ModalBody,
  ModalClose,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AvatarCropDialog } from "@/components/ui/avatar-crop-dialog";
import { useMutation } from "@tanstack/react-query";
import { uploadOrgLogo } from "@/lib/api/generated";
import { useCreateOrg } from "@/hooks/use-org";
import { Icon } from "@/components/ui/icon";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function CreateOrganization({
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: {
  /** Controlled open state; omit to let the inline trigger manage it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the inline "Create new" button (when opened from elsewhere). */
  hideTrigger?: boolean;
} = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const modalOpen = openProp ?? uncontrolledOpen;
  const setModalOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const createOrg = useCreateOrg();

  const uploadLogoMutation = useMutation({
    mutationFn: async (blob: Blob) => {
      const file = new File([blob], "logo.jpg", { type: "image/jpeg" });
      return uploadOrgLogo({ file });
    },
    onMutate: (blob) => {
      const localPreview = URL.createObjectURL(blob);
      setLogoUrl(localPreview);
      return { localPreview };
    },
    onSuccess: (result, _blob, context) => {
      if (context?.localPreview) URL.revokeObjectURL(context.localPreview);
      setLogoUrl(result.url);
    },
    onError: (_err, _blob, context) => {
      if (context?.localPreview) URL.revokeObjectURL(context.localPreview);
      setLogoUrl(null);
    },
  });

  const isUploading = uploadLogoMutation.isPending;
  const canSubmit =
    name.trim().length > 0 && !createOrg.isPending && !isUploading;
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!createOrg.isPending) submittingRef.current = false;
  }, [createOrg.isPending]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    createOrg.mutate(
      { name: name.trim(), logo_url: logoUrl },
      {
        onSuccess: () => {
          setModalOpen(false);
          setName("");
          setLogoUrl(null);
        },
      },
    );
  }, [canSubmit, createOrg, name, logoUrl, setModalOpen]);

  useHotkeys(
    "mod+enter",
    (e) => {
      e.preventDefault();
      handleSubmit();
    },
    { enabled: modalOpen, preventDefault: true, enableOnFormTags: true },
    [modalOpen, handleSubmit],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    setShowCropDialog(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCrop = useCallback(
    (blob: Blob) => {
      setShowCropDialog(false);
      if (cropSrc) {
        URL.revokeObjectURL(cropSrc);
        setCropSrc(null);
      }
      uploadLogoMutation.mutate(blob);
    },
    [cropSrc, uploadLogoMutation],
  );

  const handleCropCancel = useCallback(() => {
    setShowCropDialog(false);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    }
  }, [cropSrc]);

  const handleRemoveLogo = useCallback(() => {
    setLogoUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  return (
    <>
      <Modal open={modalOpen} onOpenChange={setModalOpen}>
        {!hideTrigger && (
          <ModalTrigger asChild>
            <Button type="button" variant="primary" size="sm">
              <Icon name="IconPlusMedium" />
              Create new
            </Button>
          </ModalTrigger>
        )}
        <ModalContent size="md">
          <ModalHeader title="New organization" iconName="IconAddKeyframe" />
          <ModalBody>
            <div className="flex flex-col gap-4">
              {/* Section label */}
              <div className="flex flex-col gap-0.5">
                <Label>
                  Organization name &amp; brand
                  <span className="text-destructive">*</span>
                </Label>
                <p className="text-sm text-contrast/60 leading-normal">
                  Add your organization name and their logo.
                </p>
              </div>

              {/* Logo section */}
              <div className="flex items-start gap-4">
                <Avatar className="size-[72px] shrink-0 rounded-lg">
                  {!isUploading && logoUrl ? (
                    <AvatarImage src={logoUrl} alt="" className="rounded-lg" />
                  ) : null}
                  <AvatarFallback className="rounded-lg">
                    <AnimatePresence mode="wait">
                      {isUploading ? (
                        <motion.div
                          key="loader"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2 }}
                          className="flex size-full items-center justify-center"
                        >
                          <Icon
                            name="IconLoadingCircle"
                            size={24}
                            className="animate-spin text-heading opacity-40"
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="placeholder"
                          className="flex size-full items-center justify-center"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Icon
                            name="IconBuildings"
                            size={32}
                            className="text-heading opacity-20"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-col justify-between self-stretch flex-1">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-heading leading-5">
                      Brand
                    </span>
                    <span className="text-xs text-contrast/60">
                      We only support PNGs and JPEGs under 10MB
                    </span>
                  </div>

                  <div className="flex items-start gap-3">
                    <AnimatePresence mode="wait">
                      {logoUrl && !isUploading ? (
                        <motion.div
                          key="actions"
                          className="flex items-start gap-3"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => fileRef.current?.click()}
                            className="flex items-center gap-1"
                          >
                            <Icon name="IconCamera1" />
                            Upload logo
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={handleRemoveLogo}
                            className="flex items-center gap-1"
                          >
                            <Icon
                              name="IconTrashCanSimple"
                              size={16}
                              className="text-heading opacity-70"
                            />
                            Remove logo
                          </Button>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="upload"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.15 }}
                        >
                          <SubmitButton
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              !isUploading && fileRef.current?.click()
                            }
                            disabled={isUploading}
                            loading={isUploading}
                            loadingLabel="Uploading"
                            className="flex items-center gap-1"
                          >
                            <Icon name="IconCamera1" />
                            Upload logo
                          </SubmitButton>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              {/* Name input */}
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Organization name"
                maxLength={255}
                autoFocus
                variant="modal"
                onKeyDown={(e: ReactKbEvent) => {
                  if (e.key === "Enter" && !e.metaKey && !e.ctrlKey)
                    handleSubmit();
                }}
                className="w-full h-9 rounded-lg border-contrast/15 px-3 text-sm placeholder:text-contrast/40 focus:border-primary"
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalClose asChild>
              <Button type="button" variant="secondary" size="sm">
                Cancel
              </Button>
            </ModalClose>
            <SubmitButton
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={createOrg.isPending}
              loadingLabel="Creating"
            >
              Create &amp; Continue
            </SubmitButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AvatarCropDialog
        open={showCropDialog}
        imageSrc={cropSrc}
        onCrop={handleCrop}
        onCancel={handleCropCancel}
        cropShape="rect"
        title="Crop Logo"
      />
    </>
  );
}
